import React from "react";
import moment from "moment";
import { useTranslation } from "react-i18next";
import { ReviewAddress } from "./ReviewAddress";
import {
  RegisterReviewSection,
  RegisterReviewSectionContent,
  RegisterReviewSectionTitle,
} from "./Register.style";
import { phoneMaskWithoutCountryCode } from "../../../shared/utilities/formUtilities";

export const ReviewInformation = (props: any) => {
  const { t } = useTranslation();

  const Section = RegisterReviewSection;
  const SectionTitle = RegisterReviewSectionTitle;
  const SectionContent = RegisterReviewSectionContent;
  const { formData, adminFee, applicationFee, role, deposit } =
    props?.options || {};

  const formatDate = (value?: string) =>
    value ? moment(value).format("MM/DD/YYYY") : "";

  const formatPhone = (value?: string) => {
    if (!value) return "";
    const masked = phoneMaskWithoutCountryCode(value);
    return masked || value;
  };

  const formatCurrency = (value?: number | string) => {
    if (value === undefined || value === null || value === "") return "";
    const numeric = typeof value === "string" ? Number(value) : value;
    if (Number.isNaN(numeric)) return "";
    return new Intl.NumberFormat("en-US", {
      style: "currency",
      currency: "USD",
      minimumFractionDigits: 0,
      maximumFractionDigits: 2,
    }).format(numeric);
  };

  const idTypesTranslations = (type: string) => {
    switch (type) {
      case "drivers_license":
        return t("account.leaseApplication.driversLicense");
      case "passport":
        return t("account.leaseApplication.passport");
      case "id_card":
        return t("account.leaseApplication.idCard");
      case "temporary_visa":
        return t("account.leaseApplication.temporaryVisa");
      case "residency_permit":
        return t("account.leaseApplication.residencyPermit");
      default:
        return type;
    }
  };

  const {
    vehicles,
    hasVehicles,
    pets,
    currentEmployment,
    currentAddress,
    applicantInfo,
    otherApplicants,
    hasOtherOccupants,
    guarantors,
    incomeFrequency,
    incomeAmount,
  } = formData || {};

  return (
    <>
      <Section>
        <SectionTitle>
          {t("account.leaseApplication.applicantInfo")}
        </SectionTitle>
        <SectionContent>
          <p>
            {t("account.leaseApplication.name")}: {applicantInfo?.name}
          </p>
          <p>
            {t("account.leaseApplication.email")}: {applicantInfo?.email}
          </p>
          <p>
            {t("account.leaseApplication.phone")}:{" "}
            {formatPhone(applicantInfo?.phone)}
          </p>
          <p>
            {t("account.leaseApplication.birthdate")}:{" "}
            {formatDate(applicantInfo?.birthdate)}
          </p>
        </SectionContent>
        <SectionTitle>
          {t("account.leaseApplication.identification")}
        </SectionTitle>
        <SectionContent>
          {/* <p>
            {t("account.leaseApplication.usCitizen")}:{" "}
            {applicantInfo?.identification?.usCitizen
              ? t("account.leaseApplication.yes")
              : t("account.leaseApplication.no")}
          </p> */}
          <p>
            {t("account.leaseApplication.type")}:{" "}
            {idTypesTranslations(applicantInfo?.identification?.type)}
          </p>
          <p>
            {t("account.leaseApplication.number")}:{" "}
            {applicantInfo?.identification?.number}
          </p>
          {applicantInfo.identification.ssn && (
            <p>
              {t("account.leaseApplication.ssn")}: XXX-XX-
              {applicantInfo.identification.ssn.substring(7)}
            </p>
          )}
        </SectionContent>

        <SectionTitle>
          {t("account.leaseApplication.emergencyContactTitle")}
        </SectionTitle>
        <SectionContent>
          <p>
            {t("account.leaseApplication.name")}:{" "}
            {applicantInfo?.emergencyContact?.name}
          </p>
          <p>
            {t("account.leaseApplication.relationship")}:{" "}
            {applicantInfo?.emergencyContact?.relationship}
          </p>
          <p>
            {t("account.leaseApplication.phone")}:{" "}
            {applicantInfo?.emergencyContact?.phone
              ? formatPhone(applicantInfo?.emergencyContact?.phone)
              : ""}
          </p>
          {/* <p>
            {t("account.leaseApplication.email")}:{" "}
            {applicantInfo?.emergencyContact?.email}
          </p> */}
          {/* <ReviewAddress address={applicantInfo?.emergencyContact?.address} /> */}
        </SectionContent>
      </Section>

      <Section>
        <SectionTitle>
          {t("account.leaseApplication.currentAddressTitle")}
        </SectionTitle>
        <SectionContent>
          <ReviewAddress address={currentAddress?.address} />
          <p>
            {t("account.leaseApplication.moveInDate")}:{" "}
            {formatDate(currentAddress?.moveInDate)}
          </p>
          <p>
            {t("account.leaseApplication.reasonForLeaving")}:{" "}
            {currentAddress?.reasonForLeaving}
          </p>
          <p>
            {t("account.leaseApplication.areYouRenting")}:{" "}
            {currentAddress?.rented
              ? t("account.leaseApplication.yes")
              : t("account.leaseApplication.no")}
          </p>
          <p>
            {t("account.leaseApplication.landlord")}: {currentAddress?.landlord}
          </p>
          <p>
            {t("account.leaseApplication.landlordContact")}:{" "}
            {currentAddress?.landlordContact}
          </p>
          <p>
            {t("account.leaseApplication.landlordPhone")}:{" "}
            {currentAddress?.landlordPhone
              ? formatPhone(currentAddress?.landlordPhone)
              : ""}
          </p>
        </SectionContent>
      </Section>

      <Section>
        <SectionTitle>
          {t("account.leaseApplication.currentEmploymentTitle")}
        </SectionTitle>
        <SectionContent>
          <p>
            {t("account.leaseApplication.employment", "Income")}:{" "}
            {formatCurrency(incomeAmount)}
          </p>
          <p>
            {t("account.leaseApplication.incomeFrequency", "Income Frequency")}:{" "}
            {incomeFrequency}
          </p>
          <p>
            {t("account.leaseApplication.industry")}:{" "}
            {currentEmployment?.industry}
          </p>
          <p>
            {t("account.leaseApplication.employer")}:{" "}
            {currentEmployment?.employer}
          </p>
          <p>
            {t("account.leaseApplication.title")}: {currentEmployment?.title}
          </p>
          <p>
            {t("account.leaseApplication.startDate")}:{" "}
            {formatDate(currentEmployment?.dateFrom)}
          </p>
          <p>
            {t("account.leaseApplication.supervisor")}:{" "}
            {currentEmployment?.superVisorName}
          </p>
          <p>
            {t("account.leaseApplication.supervisorPhone")}:{" "}
            {currentEmployment?.superVisorPhone
              ? formatPhone(currentEmployment?.superVisorPhone)
              : ""}
          </p>
          <p>
            {t("account.leaseApplication.supervisorEmail")}:{" "}
            {currentEmployment?.email}
          </p>
          <ReviewAddress address={currentEmployment?.address} />
        </SectionContent>
      </Section>

      <Section>
        <SectionTitle>{t("account.leaseApplication.pets")}</SectionTitle>
        <SectionContent>
          {!pets?.length && <p>{t("account.leaseApplication.noPets")}</p>}
          {pets?.length ? (
            pets?.map((p: any, index: number) => (
              <div key={index}>
                <h4>
                  {t("account.leaseApplication.name")}: {p?.name}
                </h4>
                <p>
                  {t("account.leaseApplication.type")}: {p?.type}
                </p>
                <p>
                  {t("account.leaseApplication.breed")}: {p?.breed}
                </p>
                <p>
                  {t("account.leaseApplication.color")}: {p?.color}
                </p>
                <p>
                  {t("account.leaseApplication.age")}: {p?.age}
                </p>
                <p>
                  {t("account.leaseApplication.weight")}: {p?.weight} lbs
                </p>
              </div>
            ))
          ) : (
            <></>
          )}
        </SectionContent>
      </Section>

      <Section>
        <SectionTitle>
          {t("account.leaseApplication.vehicleInformationTitle")}
        </SectionTitle>
        <SectionContent>
          <p>
            {t("account.leaseApplication.hasVehiclesQuestion")}:{" "}
            {hasVehicles === "Yes"
              ? t("account.leaseApplication.yes")
              : hasVehicles === "No"
              ? t("account.leaseApplication.no")
              : t("account.leaseApplication.notAnswered")}
          </p>
          {hasVehicles === "Yes" && vehicles?.length ? (
            vehicles?.map((v: any, index: number) => (
              <div key={index}>
                <p>
                  {t("account.leaseApplication.year")}: {v?.year}
                </p>
                <p>
                  {t("account.leaseApplication.make")}: {v?.make}
                </p>
                <p>
                  {t("account.leaseApplication.model")}: {v?.model}
                </p>
                <p>
                  {t("account.leaseApplication.color")}: {v?.color}
                </p>
                <p>
                  {t("account.leaseApplication.licensePlate")}:{" "}
                  {v?.licensePlateNumber} | ({v?.licensePlateState})
                </p>
              </div>
            ))
          ) : (
            <></>
          )}
        </SectionContent>
      </Section>

      <Section>
        <SectionTitle>
          {t("account.leaseApplication.householdMembersTitle")}
        </SectionTitle>
        <SectionContent>
          <p>
            {t("account.leaseApplication.willAnyoneElseLive")}:{" "}
            {hasOtherOccupants === "Yes"
              ? t("account.leaseApplication.yes")
              : hasOtherOccupants === "No"
              ? t("account.leaseApplication.no")
              : otherApplicants?.otherApplicants?.length
              ? t("account.leaseApplication.yes")
              : t("account.leaseApplication.notAnswered")}
          </p>
          {hasOtherOccupants !== "No" &&
          otherApplicants?.otherApplicants?.length ? (
            otherApplicants?.otherApplicants?.map((v: any, index: number) => (
              <div key={index}>
                {index > 0 && <p>---</p>}
                <p>
                  {t("account.leaseApplication.name")}: {v?.name}
                </p>
                <p>
                  {t("account.leaseApplication.relationship")}:{" "}
                  {v?.householdStatus}
                </p>
                {v?.householdStatus !== "Dependent" && (
                  <p>
                    {t("account.leaseApplication.email")}: {v?.email}
                  </p>
                )}
                {v?.householdStatus === "Dependent" && (
                  <p>
                    {t("account.leaseApplication.birthDate")}:{" "}
                    {formatDate(v?.birthDate)}
                  </p>
                )}
              </div>
            ))
          ) : (
            <></>
          )}
        </SectionContent>

        {guarantors?.length ? (
          <>
            <SectionTitle>
              {t("account.leaseApplication.guarantorsTitle", "Guarantors")}
            </SectionTitle>
            <SectionContent>
              {guarantors?.map((v: any, index: number) => (
                <div key={index}>
                  {index > 0 && <p>---</p>}
                  <p>
                    {t("account.leaseApplication.name")}: {v?.name}
                  </p>
                  <p>
                    {t("account.leaseApplication.email")}: {v?.email}
                  </p>
                </div>
              ))}
            </SectionContent>
          </>
        ) : null}
      </Section>

      <Section>
        <SectionTitle>
          {t("account.leaseApplication.feeAndDepositTitle")}
        </SectionTitle>
        <SectionContent>
          <p>{t("account.leaseApplication.feeReviewText")}</p>
          {deposit && deposit.amount > 0 ? (
            <p>
              {t("account.unitDetails.deposit")}:{" "}
              {formatCurrency(deposit.amount)}
            </p>
          ) : null}
          {applicationFee > 0 ? (
            <p>
              <strong>{t("account.leaseApplication.applicationFee")}</strong>:{" "}
              {formatCurrency(applicationFee)}
            </p>
          ) : null}
          {role === "Head of Household" && adminFee > 0 ? (
            <p>
              <strong>{t("account.leaseApplication.unitFee")}</strong>:{" "}
              {formatCurrency(adminFee)}
            </p>
          ) : null}
        </SectionContent>
      </Section>
    </>
  );
};
